import Request from "./Utils/Request.js";
import Share from "./Utils/Share.js";

/**
 * Class responsible for rendering dynamic content on canvases.
 */
export default class ShareGenerator {

  /**
   * Creates an instance of Share Generator class.
   */
  constructor(config, results) {

    this.config = config;
    this.results = results;
    this.persona = results.persona.name.trim().toLowerCase();
    this.era = results.era.name.trim().toLowerCase();
    // Used for complex layouts that require a second render/calculate pass
    this.recalculations = {};

    // Set the canvas size and device pixel ratio
    this.contextDefault = this.createCanvas(1200, 630, false);
    this.contextStory = this.createCanvas(1080, 1920, true);
    this.contextPost = this.createCanvas(1080, 1350, true);

  }

  /**
   * Renders the content on both canvases based on the provided content.
   */
  async render(results = null, type = null) {

    this.results = results || this.results;
    this.persona = this.results.persona.name.trim().toLowerCase();
    this.era = this.results.era.name.trim().toLowerCase();
    this.type = type || this.type;

    switch (this.type) {
      case "lowest":
        this.content = this.results.lowBpm;
        break;
      case "highest":
        this.content = this.results.highBpm;
        break;
      case "longest":
        this.content = this.results.time;
        break;
    }

    await this.renderContext(this.contextDefault, "default");
    await this.renderContext(this.contextStory, "story");
    await this.renderContext(this.contextPost, "post");

  }

  /**
   * Downloads the current images by prompting the user to save.
   */
  async download(type = null, format = "story") {
    // Only download the one if specified
    if (type) {
      return await this.downloadImage(type, format, false);
    }

    // Otherwise download all

    await this.downloadImage("persona", "story");
    await this.downloadImage("lowest", "story");
    await this.downloadImage("highest", "story");
    await this.downloadImage("longest", "story");
    await this.downloadImage("era", "story");
    await this.downloadImage("legal", "story");

    await this.downloadImage("persona", "post");
    await this.downloadImage("lowest", "post");
    await this.downloadImage("highest", "post");
    await this.downloadImage("longest", "post");
    await this.downloadImage("era", "post");
    await this.downloadImage("legal", "post");

  }

  /**
   * Shares the current image to the respective social network.
   */
  async share(network, type, format = "story") {

    // Switch the functionality based on the network.
    switch (network) {
      case "twitter":
      case "facebook":
        // Set the URL for sharing
        let { url, text } = this.config.social;
        // Build the URL query params using current values
        const params = `id=${this.results.visitor.id}&type=${type}`;
        // Build the endpoint with the base URL and params
        const endpoint = `${this.config.social.endpoint}?${params}`;
        // Render the latest to canvas context
        await this.render(this.results, type);
        // Get the image as Base64 string
        const image = this.contextDefault.canvas.toDataURL("image/png");
        // Upload the share image to S3 first
        const response = await Request.put(endpoint, image, { json: false });
        // Parse the data from the response as JSON (since we passed false above)
        const { success, data } = await response.json();
        // Get the URL from the response or use the default above
        url = success ? data : url;
        // Share the URL to the respective network
        Share.url(network, url, text, "_blank");
        break;
      case "tiktok":
      case "instagram":
      default:
        // Prompt user to save the multiple downloads
        this.download(type, format);
    }

  }

  /**
   * Renders the content on the specified canvas based on the provided content.
   */
  async renderContext(context, format = "story") {

    // Set the current format being rendered
    this.format = format;

    // Update the shorthand for offsets
    this.offsets = this.config.offsets[this.format];
    this.size = this.config.style.size[this.format];

    // Set the initial Y offset for the first element (logo)
    // Updated by each render method below to keep track of the Y offset
    let y = this.offsets.top;

    // Reset the canvas to the solid background color
    this.resetCanvas(context, this.config.colors.background);

    // Render the static layouts using precomposed images only
    switch (this.type) {
      case "persona":
      case "era":
      case "legal":
        return await this.renderLayoutStatic(context, y);
    }

    // Otherwise render the dynamic layouts using live text

    // Draw the background image behind
    await this.renderBackground(context, y);

    // Draw the logo first in the top left corner
    y = await this.renderComponentLogo(context, y);

    // Handle edge cases for long titles on default OG layouts
    if (this.format === "default") {
      switch (this.content.artwork.id) {
        // Shift upwards slightly to prevent copyright overlap
        case "431862":
          y -= 20;
          break;
        // Shift upwards slightly to prevent copyright overlap
        case "922814":
          y -= 25;
          break;
      }
    }

    // Draw the artist/title of the artwork with the separating line
    y = await this.renderComponentHeading(context, y);

    // Draw the result (made your heart race, etc.) and value (80 BPM, 30 MIN, etc.)
    y = await this.renderComponentResults(context, y);

    // Draw the image and legal line, with responsive size based on available height
    y = await this.renderComponentImage(context, y);

    // OG layout requires a second pass to re-center all content
    if (this.format === "default") {
      if (this.isRecalculating) {
        // Reset the data to stop render loop
        this.isRecalculating = false;
        this.recalculations = {};
      } else if (this.recalculations !== {}) {
        // Re-render using any recalculations specified
        this.isRecalculating = true;
        await this.renderContext(context, format);
      }
    }

  }

  /**
   * Renders the static layout on the specified canvas based on the provided content.
   */
  async renderLayoutStatic(context, y) {

    const padding = this.offsets.padding;
    const colors = this.config.colors;
    const { width, height } = context.canvas;

    // Draw the static layout based on the type
    switch (this.type) {

      case "persona":
        // Draw the static persona image
        await this.drawImage(
          context,
          `personas/ago-artrate-social-persona-${this.persona}-${this.format}.png`,
          { x: 0, background: true }
        );
        break;

      case "era":
        // Draw the background image behind
        await this.renderBackground(context, y);
        // Draw the content overlay above
        await this.drawImage(
          context,
          `eras/ago-artrate-social-era-${this.era}-${this.format}.png`,
          { x: 0, background: true }
        );
        break;

      case "legal":
        if (this.format !== "default") {
          await this.drawImage(
            context,
            `ago-artrate-social-legal-${this.format}.png`,
            { x: 0 }
          );
        }
        break;

    }

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: padding, height, color: colors.debug });
      this.drawLine(context, { x: context.canvas.width - padding, height, color: colors.debug });
      this.drawLine(context, { x: 0, y: y, width, color: colors.debug });
      const bottom = this.format === "default" ? 95 : padding;
      this.drawLine(context, { x: 0, y: context.canvas.height - bottom, width, color: colors.debug });
    }

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentLogo(context, y) {

    const padding = this.offsets.padding;

    // Draw the logo first in the top left corner
    if (this.format === "default") {
      await this.drawImage(context, "ago-artrate-social-logo-project.png", {
        y: y + 2,
        x: 1035,
        scale: 0.5,
      });
      y = (await this.drawImage(context, "ago-artrate-social-logo-brand.png", {
        y: y,
        x: padding,
        scale: 0.5,
      })).y;
    } else {
      y = (await this.drawImage(context, "ago-artrate-social-logo.png", {
        y: y,
        x: padding,
      })).y;
    }

    // Return the latest y offset to use subsequently
    return y;

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentHeading(context, y) {

    const { width, margin } = this.offsets.keyline;
    const isDefault = this.format === "default";
    const colors = this.config.colors;

    // The left can be altered in the second render for OG to center it
    let left = this.offsets.left;
    // If the recalculation provides a new value, use it
    if (isDefault && this.recalculations.left) {
      left -= this.recalculations.left;
    }

    // Check the title width to determine if line break should be used
    // Longer titles skip the line break before artist name to save space
    const titleWidth = this.calculateTextWidth(
      context,
      this.content.artwork.image_title,
      { weight: 600, spacing: this.config.style.spacing[this.format].heading }
    );

    // No line break if title is longer than one line
    if (titleWidth > this.offsets.keyline.width) {
      // Draw the title and artist of the artwork (passing true with join them)
      y = await this.renderComponentArtist(context, y, true);
    } else {
      // Draw the title of the artwork
      y = await this.renderComponentTitle(context, y);
      // Draw the artst of the artwork
      y = await this.renderComponentArtist(context, y, false);
    }

    // Draw the border line below the title/artist
    this.drawLine(context, { x: left, y: y + margin, width });

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y, width: context.canvas.width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + margin, width: context.canvas.width, color: colors.debug });
      // The vertical line for the title/artist border line
      this.drawLine(context, { x: left + width, height: context.canvas.height, color: colors.debug });
      this.drawLine(context, { x: left, height: context.canvas.height, color: colors.debug });
    }

    // Return the latest y offset to use subsequently
    return y;

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentTitle(context, y) {

    const isDefault = this.format === "default";
    const offset = this.offsets.title;
    const colors = this.config.colors;

    // The left can be altered in the second render for OG to center it
    let left = this.offsets.left;
    // If the recalculation provides a new value, use it
    if (isDefault && this.recalculations.left) {
      left -= this.recalculations.left;
    }

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y, width: context.canvas.width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + offset, width: context.canvas.width, color: colors.debug });
    }

    let width = this.offsets.keyline.width;

    // Draw the title of the artwork
    y = this.drawText(context, this.content.artwork.image_title, {
      x: left,
      y: y + offset,
      weight: 600,
      width: width,
      spacing: this.config.style.spacing[this.format].heading,
    });

    // Return the latest y offset to use subsequently
    return y;

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentArtist(context, y, prependTitle = false) {

    const isDefault = this.format === "default";
    const width = context.canvas.width;
    const colors = this.config.colors;

    const spacing = isDefault ? 10 : 20;
    const offset = prependTitle ? this.offsets.title : spacing;

    // The left can be altered in the second render for OG to center it
    let left = this.offsets.left;
    // If the recalculation provides a new value, use it
    if (isDefault && this.recalculations.left) {
      left -= this.recalculations.left;
    }

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y, width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + offset, width, color: colors.debug });
    }

    // Start with the artist text
    let text = [{
      color: colors.artist,
      text: "by " + this.content.artwork.image_artist.trim(),
    }];

    // Optionally prepend the title (if rendering without line break)
    if (prependTitle) {
      text.unshift({
        weight: 600,
        text: this.content.artwork.image_title.trim() + " ",
      });
    }

    let textWidth = this.offsets.keyline.width;

    // Handle edge cases for long titles on default OG layouts
    if (isDefault) {
      switch (this.content.artwork.id) {
        // Widen the text slightly to prevent copyright overlap
        case "431862":
          textWidth += 40;
          text[0].text += " ";
          break;
      }
    }

    // Draw the artst of the artwork
    y = this.drawText(context, text, {
      x: left,
      y: y + offset,
      width: textWidth,
      height: spacing,
      spacing: this.config.style.spacing[this.format].heading,
    });

    // Return the latest y offset to use subsequently
    return y;

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentResults(context, y) {

    const isDefault = this.format === "default";
    const isBPM = this.type !== "longest";
    const unit = isBPM ? "BPM" : "MIN";
    const icon = isBPM ? "rate" : "time";
    const margin = this.offsets.keyline.margin * 2;
    const width = context.canvas.width;
    const colors = this.config.colors;
    let text = "";

    // The left can be altered in the second render for OG to center it
    let { left, action } = this.offsets;
    // If the recalculation provides a new value, use it
    if (isDefault && this.recalculations.left) {
      left -= this.recalculations.left;
    }

    switch (this.type) {
      case "highest":
        text = "Made your heart race";
        break;
      case "lowest":
        text = "Made your heart go slo-mo";
        break;
      case "longest":
        text = "Stopped you in your tracks";
        break;
    }

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y + margin, width, color: colors.debug });
    }

    // Draw the action for the result (made your heart race, etc.)
    y = this.drawText(context, text, {
      x: left,
      y: y + margin,
      weight: 600,
      spacing: this.config.style.spacing[this.format].heading,
    });

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y, width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + action, width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + this.size.unit, width, color: colors.debug });
    }

    // Draw the numeric value (heart rate, duration)
    this.drawText(context, this.content.value, {
      x: left,
      y: y + action,
      size: this.size.value,
      weight: 600,
      spacing: this.config.style.spacing[this.format].value,
    });

    // Draw the value's unit of measruement (BPM, MIN, etc.)
    // Cache the Y offset to return at the end
    const top = this.drawText(context, unit, {
      x: left + this.offsets.unit.x + context.measureText(this.content.value).width,
      y: y + this.offsets.unit.y,
      size: this.size.unit,
      spacing: this.config.style.spacing[this.format].unit,
    });

    // Draw the icon for the unit of measurement
    await this.drawImage(context, `ago-artrate-social-icon-${icon}.png`, {
      x: left + this.offsets.icon.x,
      y: y + this.size.unit,
      scale: this.offsets.icon.scale,
    });

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: top, width, color: colors.debug });
    }

    // Return the latest y offset to use subsequently
    // Pass this instead of current Y to account for the value/unit lockup
    return top;

  }

  /**
   * Renders the component on the specified canvas based on the provided content.
   */
  async renderComponentImage(context, y) {

    const isDefault = this.format === "default";
    const isStory = this.format === "story";
    const offset = isStory ? 96 : 64;
    const padding = this.offsets.padding;
    const width = context.canvas.width;
    const colors = this.config.colors;

    // The left can be altered in the second render for OG to center it
    let { left } = this.offsets;
    // If the recalculation provides a new value, use it
    if (isDefault && this.recalculations.left) {
      left -= this.recalculations.left;
    }

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y, width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + offset, width, color: colors.debug });
    }

    // Create the legal text based on artist, title and copyright line
    const legal = [
      { style: "normal", text: this.content.artwork.image_artist + ", ", },
      { style: "normal", text: this.content.artwork.image_title + ", ", },
      { style: "normal", text: this.content.artwork.image_copyright.replace("<sup>&copy;</sup>", "©"), },
    ];

    // Various heights that need to be calculated for a responsive layout
    let heights = {};

    // The default OG layout does not include the image as part of the content column
    if (isDefault) {
      let width = this.offsets.keyline.width;
      // Handle edge cases for long titles on default OG layouts
      switch (this.content.artwork.id) {
        // Widen the text slightly to prevent copyright overlap
        case "725739":
          width += 50;
          break;
        // Widen the text slightly to prevent copyright overlap
        case "431862":
          width += 75;
          break;
        // Widen the text slightly to prevent copyright overlap
        case "922814":
          width += 150;
          break;
      }
      // Get the height of the legal text to use in calculating image height
      heights.legal = this.calculateTotalTextHeight(context, legal.map(({ text }) => text).join(""), {
        size: this.size.legal,
        width: width,
        height: 6,
        spacing: this.config.style.spacing[this.format].legal,
      });
      // The max height is dynamic based on available space for non default (OG) layouts
      heights.max = 440;
      heights.text = -16 - heights.legal;
    } else {
      // Get the height of the legal text to use in calculating image height
      heights.legal = this.calculateTotalTextHeight(context, legal.map(({ text }) => text).join(""), {
        size: this.size.legal,
        width: context.canvas.width - (padding * 2),
        height: 6,
        spacing: this.config.style.spacing[this.format].legal,
      });
      heights.max = context.canvas.height - y - offset - padding - heights.legal - this.size.legal;
      heights.text = this.size.legal;
    }

    const cached = y;
    const min = cached + 60;

    // Draw the artwork image asset in default OG format
    if (isDefault) {

      // The safe space width for the image to render within
      const maxWidth = context.canvas.width
        - this.offsets.artwork.left
        - this.offsets.artwork.right
        - this.offsets.artwork.padding
        - this.offsets.keyline.width;
      // The safe space height for the image to render within
      const maxHeight = context.canvas.height
        - this.offsets.artwork.top
        - this.offsets.artwork.bottom;

      // Render the image and cache the results for secondary calculations
      const result = await this.drawImage(context, "artwork/" + this.content.artwork.image_filename, {
        x: this.offsets.artwork.left,
        y: this.offsets.artwork.top,
        width: maxWidth,
        height: maxHeight,
        fit: true,
        recalculations: this.recalculations,
      });

      // This allows a second render to recenter all content
      if (!this.isRecalculating) {
        // Only required if image does not fill the full width
        if (result.width < maxWidth) {
          this.recalculations.left = (result.x - this.offsets.artwork.left) / 2;
        }
        if (result.height < maxHeight) {
          this.recalculations.top = (maxHeight - result.height) / 2;
        }
      }

      // Update the Y offset and clamp to the min value
      y = result.y < min ? min : result.y;

    // Draw the artwork image asset in story/post format
    } else {
      // Update the Y offset using the result value
      y = (await this.drawImage(context, "artwork/" + this.content.artwork.image_filename, {
        x: this.offsets.left,
        y: y + offset,
        full: true,
        max: heights.max,
      })).y;
    }

    let legalWidth = this.offsets.keyline.width;
    let legalOffsetY = 0;

    // Handle edge cases for long titles on default OG layouts
    if (isDefault) {
      switch (this.content.artwork.id) {
        // Widen the text slightly to prevent copyright overlap
        case "725739":
          legalWidth += 50;
          break;
        // Widen the text slightly to prevent copyright overlap
        case "431862":
          legalWidth += 50;
          legalOffsetY += 15;
          break;
        // Widen the text slightly to prevent copyright overlap
        case "922814":
          legalWidth += 150;
          break;
      }
    }

    // Draw the legal line
    let top = this.drawText(context, legal, {
      x: left,
      y: y + heights.text + legalOffsetY,
      size: this.size.legal,
      width: isDefault ? legalWidth : context.canvas.width - (padding * 2),
      height: 6,
      color: colors.legal,
      spacing: this.config.style.spacing[this.format].legal,
    });

    // HACK: For debugging purposes only
    if (this.config.debug) {
      this.drawLine(context, { x: 0, y: y, width, color: colors.debug });
      this.drawLine(context, { x: 0, y: y + heights.text, width, color: colors.debug });
      if (this.format === "default") {
        this.drawLine(context, { x: 0, y: top, width, color: colors.debug });
      } else {
        this.drawLine(context, { x: 0, y: y  + this.size.legal + heights.legal, width, color: colors.debug });
      }
    }

    // Return the latest y offset to use subsequently
    return y;

  }

  /**
   * Renders the background on the specified canvas based on the provided content.
   */
  async renderBackground(context, y) {

    const padding = this.offsets.padding;
    const { width, height } = context.canvas;
    const colors = this.config.colors;

    // Draw the background image behind
    await this.drawImage(
      context,
      `backgrounds/ago-artrate-social-background-${this.persona}-${this.format}.jpg`,
      { x: 0 }
    );

    // HACK: For debugging purposes only
    if (this.config.debug) {
      // The left vertical line for the padding
      this.drawLine(context, { x: padding, height, color: colors.debug });
      // The right vertical line for the padding
      this.drawLine(context, { x: width - padding, height, color: colors.debug });
      // The top horizontal line for the padding
      this.drawLine(context, { x: 0, y: y, width, color: colors.debug });
    }

  }

  /**
   * Draws the live text on the canvas context.
   * @param {CanvasRenderingContext2D} context - The canvas rendering context.
   * @param {string} text - The text to be displayed.
   * @param {Object} options - Configuration options for drawing the live text.
   */
  drawText(context, value, options = {}) {

    const padding = this.offsets.padding;

    let {
      font = this.config.style.font,
      size = this.size.heading,
      weight = 400,
      height = 0,
      baseline = "top",
      color = this.config.colors.foreground,
      width = 0,
      spacing = 0,
      x = this.offsets.left,
      y = 0,
    } = options;

    // Set the text baseline value for vertical alignment
    context.textBaseline = baseline;

    // Wrap the value in an array/object if text or number is passed
    if (typeof(value) === "string" || typeof(value) === "number") {
      value = [{ style: "normal", weight, text: value }];
    }

    // Fall back to the safe area if no width specified
    if (width <= 0) {
      width = context.canvas.width - (padding * 2);
    }

    // Set the initial values to adjust below
    let left = x;
    let top = y;
    let lineWidth = 0;

    // Handle words/fragments individually to support formatting
    for (let lineIndex = 0; lineIndex < value.length; lineIndex++) {
      // Get the formatting values from the current text object
      const current = value[lineIndex];
      const style = current.style || "normal";
      const bold = current.weight || weight;
      const fill = current.color || color;
      // Set the font each time to change the formatting
      context.font = `${style} ${bold} ${size}px ${font}`;
      // Set the color to render the text in
      context.fillStyle = fill;
      // Handles passing in raw numerics for the values
      const words = current.text.toString().split(" ");
      // Measure the text to manually wrap at the specified max width
      for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
        // The word includes a space at the end
        const word = words[wordIndex]
          + (wordIndex >= words.length - 1 ? "" : " ");
        // The characters that make up the word
        const characters = word.split("");
        // Calculate word with based on spacing offset and character count
        const wordWidth = context.measureText(word).width
          + (characters.length * spacing);
        // Increment the line width based on the word width
        lineWidth += wordWidth;
        // If the line becomes too long, break down to a new line
        if (lineWidth > width && wordIndex > 0) {
          top += this.calculateFontHeight(context.font) + height;
          left = x;
          lineWidth = wordWidth;
        }
        // Iterate over the characters to render with spacing offset
        // This allows for letter-spacing like functionality
        characters.forEach((character) => {
          // Measure the width of the word with the space
          const characterWidth = context.measureText(character).width + spacing;
          // Render the text at the specified position
          context.fillText(character, left, top);
          // Update the left position with the current word width
          left += characterWidth;
        });
      }
    }

    return top + this.calculateFontHeight(context.font);

  }

  /**
   * Draws an image on the canvas context.
   * @param {CanvasRenderingContext2D} context - The canvas rendering context.
   * @param {Object} options - Configuration options for drawing the image.
   */
  drawImage(context, src, options = {}) {

    const padding = this.offsets.padding;

    let {
      x = this.offsets.left,
      y = 0,
      width = -1,
      height = -1,
      max = 0,
      full = false,
      background = false,
      scale = 1,
      fit = false,
      recalculations = {},
    } = options;

    // Use a promise to allow waiting for the image to load
    return new Promise((resolve, reject) => {

      // Create the image instance
      const image = new Image();

      // Wait for the image to fully load before getting the dimensions
      image.onload = () => {
        // Handle dynamically fitting the image into the OG safe space
        if (fit) {
          ({ x, y, width, height } = this.fitImage(image, x, y, width, height));
        }
        if (width < 0) {
          // Image is full width minus the padding on each side
          width = full
            ? context.canvas.width - (2 * padding)
            : image.width;
        }
        if (height < 0) {
          // Height is proportional to the width
          height = full
            ? (image.height / image.width) * width
            : image.height;
        }
        // If using full width, ensure max height is not exceeded
        if (full) {
          // Check if the height exceeds max
          if (max && height > max) {
            // Calculate new width to maintain aspect ratio
            width = (max / image.height) * image.width;
            height = max;
          }
        }
        // Handle full sized background images
        if (background) {
          width = context.canvas.width;
          height = context.canvas.height;
        }
        // Handle the optional scaling
        width *= scale;
        height *= scale;
        // These are used to offset position on second pass
        // Some layouts require two renders to calculate offsets correctly
        if (recalculations.left) {
          x -= recalculations.left;
        }
        if (recalculations.top) {
          y += recalculations.top;
        }
        // Draw the image at the specified position
        context.drawImage(image, x, y, width, height);
        // Round upwards to avoid pixel fractions
        resolve({ x, y: Math.ceil(y + height), width, height });
      };

      // TODO: Determine if better error handling is required
      image.onerror = () => reject({ x, y, width, height });

      // Set the source after setting the onload handler to cover race conditions
      try {
        image.src = this.config.assets + src;
      } catch (exception) {
        reject({ x, y, width, height });
      }

    });

  }

  /**
   * Draws a border line on the canvas context.
   * @param {CanvasRenderingContext2D} context - The canvas rendering context.
   * @param {Object} options - Configuration options for drawing the line.
   */
  drawLine(context, options = {}) {

    const {
      x = this.offsets.left,
      y = 0,
      color = this.config.colors.border,
      thickness = 1,
      width = 0,
      height = 0,
    } = options;

    context.strokeStyle = color;
    context.lineWidth = thickness;

    // Begin a new path
    context.beginPath();
    // Move the drawing cursor to the start point
    context.moveTo(x, y);
    // Draw a line to the end point using the width
    context.lineTo(x + width, y + height);
    // Apply the stroke (draw the line)
    context.stroke();

  }

  /**
   * Sets the canvas size and scales it based on device pixel ratio.
   * @param {HTMLCanvasElement} canvas - The canvas element.
   * @param {number} width - The width in CSS pixels.
   * @param {number} height - The height in CSS pixels.
   */
  createCanvas(width, height, scale = true) {

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    // Set the canvas width/height accounting for pixel density
    canvas.width = width;
    canvas.height = height;

    // Don't scale the OG image for sharing
    if (scale) {
      canvas.style.width = `${width / 2}px`;
      canvas.style.height = `${height / 2}px`;
    }

    // Optionally append to the DOM
    const container = document.querySelector(this.config.container);
    if (container) {
      container.appendChild(canvas);
    }

    // Return the context for internal reference
    return context;

  }

  /**
   * Clears the canvas context to reset to default, with optional background color.
   */
  resetCanvas(context, color) {

    context.clearRect(0, 0, context.canvas.width, context.canvas.height);

    if (color) {
      context.fillStyle = color;
      context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    }

  }

  /**
   * Fits the image with the space provided.
   */
  fitImage(image, x, y, width, height) {

    // Get image dimensions
    const imgWidth = image.naturalWidth;
    const imgHeight = image.naturalHeight;

    // Calculate aspect ratios
    const imgAspect = imgWidth / imgHeight;
    const spaceAspect = width / height;

    let scaledWidth;
    let scaledHeight;

    // Determine scale based on aspect ratio
    if (imgAspect > spaceAspect) {
        // Image is wider than space
        scaledWidth = width;
        scaledHeight = width / imgAspect;
    } else {
        // Image is taller or the same aspect ratio as space
        scaledHeight = height;
        scaledWidth = height * imgAspect;
    }

    // Calculate position to align to the top-right
    return {
      x: x + width - scaledWidth,
      y: y,
      width: scaledWidth,
      height: scaledHeight,
    };

  }

  /**
   * Downloads the images from the canvases for the specific type.
   */
  async downloadImage(type, format, urlOnly = false) {

    const list = ["persona", "lowest", "highest", "longest", "era", "legal"];
    const index = list.indexOf(type) + 1;

    await this.render(this.results, type);

    const context = format === "post" ? this.contextPost : this.contextStory;

    return await this.downloadCanvas(
      context.canvas,
      `ago-artrate-social-${this.persona}-${format}-${index}-${type}.png`,
      urlOnly
    );

  }

  /**
   * Creates a download link for a canvas image and prompts for download.
   */
  async downloadCanvas(canvas, filename, urlOnly = false) {

    if (urlOnly) {
      return new Promise((resolve, reject) => {
        // Convert canvas image to a Blob
        canvas.toBlob((blob) => resolve(URL.createObjectURL(blob)));
      });
    }

    const link = document.createElement("a");

    // Convert canvas to Data URL
    link.href = canvas.toDataURL("image/png");
    // Set the filename
    link.download = filename;

    // Simulate click on temp element to prompt the download and then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  }

  /**
   * Calculates the height of the font.
   */
  calculateFontHeight(font) {

    // Create a temporary div to measure text height
    const div = document.createElement("div");
    div.style.font = font;
    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.innerText = "AGO";
    document.body.appendChild(div);

    const height = div.offsetHeight;
    document.body.removeChild(div);

    // HACK: This addresses the extra space caused by crappy fake "web" font
    return height * this.config.style.textHeightHack;

  }

  /**
   * Calculates the total height of the text wrapped to a max width.
   */
  calculateTotalTextHeight(context, text, options = {}) {

    const padding = this.offsets.padding;

    const {
      font = this.config.style.font,
      size = this.size.heading,
      weight = 400,
      height = 0,
      width = context.canvas.width - (padding * 2),
      spacing = 0,
    } = options;

    context.font = `${weight} ${size}px ${font}`;

    // Trim the value after ensuring it is a string
    // This covers edge cases with spaces at the start/end
    // Also handles passing in raw numerics for the values
    const words = text.toString().trim().split(" ");
    let content = "";
    let y = 0;
    let total = 0;

    // Measure the text to manually wrap at the specified max width
    for (let index = 0; index < words.length; index++) {
      const word = words[index] + (index >= words.length - 1 ? "" : " ");
      const characters = word.split("").length;
      const current = context.measureText(word).width + (spacing * characters);
      if (total + current > width && index > 0) {
        y += this.calculateFontHeight(context.font) + height;
        total = 0;
        content = "";
      }
      total += current;
      content += word;
    }

    return y + this.calculateFontHeight(context.font);

  }

  /**
   * Calculates the width of the text.
   */
  calculateTextWidth(context, text, options = {}) {

    const {
      font = this.config.style.font,
      size = this.size.heading,
      weight = 400,
      spacing = 0,
    } = options;

    context.font = `${weight} ${size}px ${font}`;

    const characters = text.split("");

    return context.measureText(text + " ").width + (characters.length * spacing);

  }

}
